import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { NavigationSidebar } from '@src/components/NavigationSidebar';
import { ShowcaseTrigger } from '@src/components/Showcase';
import { Header } from '@src/containers/Header';
import { NoMatchPage } from '@src/containers/NoMatchPage';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import { ShowcaseName } from '@src/tokens/showcase';
import { getDisplayMode } from '@src/utils/getDisplayMode';
import {
  ACTION,
  AppwideDrawer,
  Checkbox,
  Dialog,
  Flex,
  Grid,
  isSandboxEnv,
  LoaderTalos,
  MixpanelEvent,
  MixpanelEventProperty,
  Notes,
  useAppwideDrawerContext,
  useEventListener,
  useGlobalDialog,
  useMixpanel,
} from '@talos/kyoko';
import lazyWithPrefetch from '@talos/kyoko/src/utils/lazyWithPrefetch';
import { noop } from 'lodash-es';
import { memo, startTransition, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { AvaAIDrawer } from '../../components/AvaAI/AvaAIDrawer';
import { AvaAIPopout } from '../../components/AvaAI/AvaAIPopout';
import { AvaAIShortcut } from '../../components/AvaAI/AvaAIShortcut';
import { OMSDependencies } from '../../components/OMS/OMSDependencies';
import { useAdminHeaderContext } from '../../providers/AdminHeaderContext';
import { useAppStateSelector } from '../../providers/AppStateProvider';
import { useAvaAIVisibility } from '../../providers/AvaAIContext';
import { useDisplaySettings } from '../../providers/DisplaySettingsProvider';
import { canAccessLegacyPortfolioEngineeringRedirect, canAccessPortfolioNav } from '../../tokens/navigationPaths';
import { AnalyticsCanonicalRedirect } from './AnalyticsLegacyRedirect';
import { ForcedDesktopModeBanner } from './ForcedDesktopModeBanner';
import { LegacyPortfolioRedirect } from './LegacyPortfolioRedirect';
import { NotificationDeepLinkRedirect } from './NotificationDeepLinkRedirect';
import { TransfersRouteRedirect } from './TransfersRouteRedirect';

export const Content = styled(Grid)`
  flex: 1 1 0;
  min-height: 0;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  overflow: hidden;
  gap: ${({ theme }) => theme.spacingLayout * 2}px;
  background: ${({ theme }) => theme.backgroundBody};
  grid-template-areas:
    'sidebar header'
    'sidebar body';
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
  width: 100%;
`;

const Body = styled(Flex)<{ $hasDrawerColumn?: boolean }>`
  grid-area: body;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  gap: ${({ theme, $hasDrawerColumn }) => ($hasDrawerColumn ? theme.spacingLayout * 2 : 0)}px;
`;

const MainArea = styled(Flex)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  gap: ${({ theme }) => theme.spacingLayout}px;
`;

const DrawerArea = styled(Flex)`
  height: 100%;
  overflow: hidden;
  flex: 0 0 auto;
  min-width: 0;
`;

const TalosAdminContainer = lazyWithPrefetch(() => import('@src/containers/TalosAdmin'));
const AdminContainer = lazyWithPrefetch(() => import('@src/containers/Admin'));
const PortfolioContainer = lazyWithPrefetch(() => import('@src/containers/Portfolio'));
const PortfolioEngineeringContainer = lazyWithPrefetch(() => import('@src/containers/PortfolioEngineering'));
const ResearchContainer = lazyWithPrefetch(() => import('@src/containers/Research'));
const DealerContainer = lazyWithPrefetch(() => import('@src/containers/Dealer'));
const OperationsContainer = lazyWithPrefetch(() => import('@src/containers/Operations'));
const AnalyticsContainer = lazyWithPrefetch(() => import('@src/containers/Analytics'));
const MonitoringContainer = lazyWithPrefetch(() => import('@src/containers/Monitoring'));
const SettingsContainer = lazyWithPrefetch(() => import('@src/containers/UserSettings'));
const OMS = lazyWithPrefetch(() => import('@src/components/OMS'));
const PowerSearch = lazyWithPrefetch(() => import('../../components/PowerSearch/PowerSearch'));
const CredentialsAgeBanner = lazyWithPrefetch(
  () => import('../../components/CredentialsAgeBanner/CredentialsAgeBanner')
);
const TradingRoute = lazyWithPrefetch(() => import('@src/containers/Trading/TradingRoute'));
const MonitoringRoute = lazyWithPrefetch(() => import('@src/containers/Monitoring/MonitoringRoute'));
const IntelRoute = lazyWithPrefetch(() => import('@src/containers/Intel/IntelRoute'));

// Workaround for UI-5968.
// When a new version is deployed, these files will no longer exist, and so
// we prefetch them to avoid the page crashing.
AdminContainer.prefetch();
TalosAdminContainer.prefetch();
PortfolioContainer.prefetch();
PortfolioEngineeringContainer.prefetch();
ResearchContainer.prefetch();
DealerContainer.prefetch();
OperationsContainer.prefetch();
AnalyticsContainer.prefetch();
MonitoringContainer.prefetch();
MonitoringRoute.prefetch();
IntelRoute.prefetch();
SettingsContainer.prefetch();
OMS.prefetch();
PowerSearch.prefetch();
CredentialsAgeBanner.prefetch();
TradingRoute.prefetch();

const TradeMatchRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('trade', 'trading');
  return <Navigate to={newPath} replace />;
};

export const Pages = memo(function Pages() {
  const { isAuthorized } = useRoleAuth();
  const location = useLocation();
  const mixpanel = useMixpanel();

  const path = location?.pathname;
  const section = path.split('/')[1] ?? 'trading';

  useEffect(() => {
    mixpanel.time_event(MixpanelEvent.ViewSection);
    return () => {
      mixpanel.track(MixpanelEvent.ViewSection, { [MixpanelEventProperty.Section]: section });
    };
  }, [section, mixpanel]);

  useEventListener('beforeunload', () => {
    mixpanel.track(MixpanelEvent.ViewSection, { [MixpanelEventProperty.Section]: section });
  });

  const {
    enableMonitoringBlotters,
    enableMonitoringSpaces,
    viewDealerMarginPages,
    viewCounterpartyMonitoring,
    enableSignals,
    enableAnalyticsInTrading,
    enablePortfolioManagementSystem,
    enablePMSPENavigationMigration,
  } = useFeatureFlag();
  const { content, ...dialog } = useGlobalDialog();
  const { termsAccepted } = useDisplaySettings();

  const displayMode = getDisplayMode() ?? 'browser';
  const isInstalledApp = ['standalone', 'minimal-ui'].includes(displayMode);

  const { showAdminHeader } = useAdminHeaderContext();

  const { backgroundBody, spacingLayout } = useTheme();

  const hasDrawer = useAppwideDrawerContext().isOpen;
  const hasOMS = useAppStateSelector(state => state.OMS.openedView !== undefined);

  const { isEnabled: isAvaAIEnabled, isOpen: isAvaAIOpen, isPopped: isAvaAIPopped } = useAvaAIVisibility();
  const hasAvaAIDocked = isAvaAIEnabled && isAvaAIOpen && !isAvaAIPopped;

  if (!termsAccepted && isSandboxEnv) {
    return (
      <ErrorBoundary>
        <TermsOfService />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <OMSDependencies />
      <ShowcaseTrigger showcase={ShowcaseName.v132Mobile} />
      <Dialog {...dialog}>{content}</Dialog>
      <PowerSearch />
      <Notes />
      <Suspense>
        <CredentialsAgeBanner />
      </Suspense>
      <AvaAIPopout />
      <AvaAIShortcut />
      <Content
        borderTop={!showAdminHeader && isInstalledApp ? `${spacingLayout}px solid ${backgroundBody}` : 'none'}
        transition="border-top-width 2s"
      >
        <NavigationSidebar gridArea="sidebar" />
        <Header />

        <Body $hasDrawerColumn={hasDrawer || hasOMS || hasAvaAIDocked}>
          <MainArea>
            <Suspense fallback={<LoaderTalos />}>
              <Routes>
                <Route path="/" element={<Navigate to="/trading" replace />} />
                <Route path="/trade" element={<Navigate to="/trading" replace />} />
                <Route path="/trade/*" element={<TradeMatchRedirect />} />
                <Route path="/dealer" element={<Navigate to="/dealer/monitoring" replace />} />
                <Route path="/notifications/:notificationId" element={<NotificationDeepLinkRedirect />} />
                <Route path="/trading/*" element={<TradingRoute />} />
                {isAuthorized(ACTION.SUBMIT_TRANSFER) && (
                  <Route path="/transfers" element={<TransfersRouteRedirect />} />
                )}
                {canAccessPortfolioNav(
                  isAuthorized,
                  enablePortfolioManagementSystem,
                  enablePMSPENavigationMigration
                ) && <Route path="/portfolio/*" element={<PortfolioContainer />} />}
                {enablePMSPENavigationMigration && canAccessLegacyPortfolioEngineeringRedirect(isAuthorized) ? (
                  <Route path="/portfolio-engineering/*" element={<LegacyPortfolioRedirect />} />
                ) : null}
                {!enablePMSPENavigationMigration && isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING) && (
                  <Route path="/portfolio-engineering/*" element={<PortfolioEngineeringContainer />} />
                )}
                {enablePMSPENavigationMigration && isAuthorized(ACTION.EDIT_RESEARCH) && (
                  <Route path="/research/*" element={<ResearchContainer />} />
                )}
                {isAuthorized(ACTION.VIEW_DEALER) && <Route path="/dealer/*" element={<DealerContainer />} />}
                {((enablePMSPENavigationMigration &&
                  (isAuthorized(ACTION.VIEW_PORTFOLIO) || isAuthorized(ACTION.EDIT_OPERATIONS))) ||
                  (isAuthorized(ACTION.VIEW_DEALER) && viewDealerMarginPages && viewCounterpartyMonitoring)) && (
                  <Route path="/operations/*" element={<OperationsContainer />} />
                )}
                {enableAnalyticsInTrading ? (
                  <Route path="/analytics/*" element={<AnalyticsCanonicalRedirect />} />
                ) : (
                  isAuthorized(ACTION.VIEW_ANALYTICS) && <Route path="/analytics/*" element={<AnalyticsContainer />} />
                )}
                <Route path="/settings/*" element={<SettingsContainer />} />
                {!enableMonitoringSpaces && (
                  <Route path="/monitoring" element={<Navigate to="/monitoring/blotters" replace />} />
                )}
                {enableMonitoringBlotters && !enableMonitoringSpaces && (
                  <Route path="/monitoring/*" element={<MonitoringContainer />} />
                )}
                {enableMonitoringBlotters && enableMonitoringSpaces && (
                  <Route path="/monitoring/*" element={<MonitoringRoute />} />
                )}
                {enableSignals && isAuthorized(ACTION.VIEW_INTEL) && <Route path="/intel/*" element={<IntelRoute />} />}
                <Route path="/admin/*" element={<AdminContainer />} />
                {isAuthorized(ACTION.VIEW_TALOS_ADMIN_SETTINGS) && (
                  <Route path="/talos-admin/*" element={<TalosAdminContainer />} />
                )}
                <Route path="*" element={<NoMatchPage />} />
              </Routes>
            </Suspense>
          </MainArea>
          {/*
           * Per-boundary gap spacers between drawers: CSS flex gap would also add space beside
           * zero-width closed drawers. Explicit spacers add spacing only between adjacent open drawers.
           */}
          <DrawerArea>
            <AppwideDrawer />
            {hasDrawer && (hasOMS || hasAvaAIDocked) && <Flex flexShrink={0} w={`${spacingLayout}px`} />}
            <Suspense>
              <OMS />
            </Suspense>
            {hasOMS && hasAvaAIDocked && <Flex flexShrink={0} w={`${spacingLayout}px`} />}
            <AvaAIDrawer />
          </DrawerArea>
        </Body>
      </Content>
      <ForcedDesktopModeBanner />
    </ErrorBoundary>
  );
});

function TermsOfService() {
  const { setTermsAccepted } = useDisplaySettings();
  const [checked, setChecked] = useState(false);

  const onConfirm = () => startTransition(() => setTermsAccepted(true));

  return (
    <div>
      <Dialog
        isOpen={true}
        close={noop}
        showClose={false}
        showCancel={false}
        onConfirm={onConfirm}
        confirmDisabled={!checked}
        confirmLabel="Accept and Continue"
        title="Terms of Service"
      >
        <Checkbox onChange={e => setChecked(e.target.checked)} checked={checked}>
          I have read and agree to the{' '}
          <a href={TOS_URL} rel="noreferrer" target="_blank">
            Terms of Service
          </a>{' '}
          on behalf of my organization.
        </Checkbox>
      </Dialog>
    </div>
  );
}

const TOS_URL =
  'https://vault.pactsafe.io/s/976f1d9b-c61f-468e-b31e-65b7e5086bae/core-trading-platform-trial-evaluation-terms.html';
